<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useProducts, usePublisherProductMetrics } from "@/queries/products";
import { useSessionStore } from "@/stores/session-store";
import PageHeader from "@/components/PageHeader.vue";
import CreateProductModal from "./modals/CreateProductModal.vue";

const router = useRouter();
const sessionStore = useSessionStore();

const { products } = useProducts();

// `subscriberCount`, `successRate` and `totalRuns` are real. `monthlyRevenue` is still
// backend-stubbed (no billing model) — the UI greys it out. See the hook's docstring for details.
const orgId = computed(() => sessionStore.session?.currentOrgId ?? "");
const { metricsByProduct } = usePublisherProductMetrics(orgId);

const searchQuery = ref("");
const selectedCategory = ref("all");
const selectedStatus = ref("all");
const showCreateProductModal = ref(false);

const publisherProducts = computed(() => {
  // Two-state status derived from `hasPublishedVersion`. Anything richer belongs on the version,
  // not the product (the product itself has no lifecycle field in the domain).
  let filtered = products.value.map((product) => {
    const metrics = metricsByProduct.value.get(product.id);
    const totalRuns = metrics?.totalRuns ?? 0;
    return {
      ...product,
      subscriberCount: metrics?.subscriberCount ?? 0,
      workflowCount: metrics?.workflowCount ?? 0,
      // totalRuns === 0 means the product has no recorded runs yet — render an empty-state label
      // instead of a misleading 0% so publishers don't think their product is failing.
      successRate: totalRuns > 0 ? `${metrics!.successRate.toFixed(1)}%` : "No runs yet",
      hasRuns: totalRuns > 0,
      // Placeholder until a billing model exists.
      monthlyRevenue: metrics?.monthlyRevenue ?? 0,
      status: product.hasPublishedVersion ? "Published" : "Unpublished",
    };
  });

  if (searchQuery.value) {
    filtered = filtered.filter((product) =>
      product.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  if (selectedStatus.value !== "all") {
    filtered = filtered.filter(
      (product) => product.status.toLowerCase() === selectedStatus.value
    );
  }

  return filtered;
});

function viewProductDetail(productId: string) {
  router.push(`/publisher/${productId}`);
}

function createNewProduct() {
  showCreateProductModal.value = true;
}

function onProductCreated(_data: { productId: string; projectId: string }) {
  // Cache is invalidated automatically by useCreateProduct mutation
}

// Removed inline hover handlers - using CSS instead

function getStatusClass(status: string): string {
  return status.toLowerCase() === "published" ? "bg-success" : "bg-secondary";
}
</script>

<template>
  <div>
    <PageHeader
      title="My Products"
      :breadcrumbs="[
          //add dashboard
          
        { label: 'Dashboard', href: '/' },
        { label: 'Publisher', href: '/publisher' },
        { label: 'My Products' }
      ]"
    >
      <template #actions>
        <button
          class="bb-btn-primary-cta"
          @click="createNewProduct"
          data-cy="new-product"
        >
          <i class="bi bi-plus me-2"></i>New<span class="d-none d-md-inline"> Product</span>
        </button>
      </template>
    </PageHeader>

    <!-- Search and Filters -->
    <div class="row g-3 mb-4">
      <div class="col-md-6">
        <input
          type="text"
          class="form-control"
          placeholder="Search products..."
          v-model="searchQuery"
          data-cy="search-products"
        />
      </div>
      <div class="col-md-3">
        <select
          class="form-select"
          v-model="selectedCategory"
          data-cy="filter-category"
        >
          <option value="all">All Categories</option>
          <option value="marketing">Marketing Automation</option>
          <option value="crm">CRM Integration</option>
          <option value="analytics">Analytics</option>
          <option value="data">Data Processing</option>
        </select>
      </div>
      <div class="col-md-3">
        <select
          class="form-select"
          v-model="selectedStatus"
          data-cy="filter-status"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
        </select>
      </div>
    </div>

    <!-- Products Grid -->
    <div v-if="publisherProducts.length === 0" class="empty-state">
      <div class="text-center py-5">
        <i class="bi bi-box-seam display-1 text-muted"></i>
        <h3 class="mt-3 text-muted">No products found</h3>
        <p class="text-muted">Create your first product to get started</p>
        <button class="bb-btn-primary-cta" @click="createNewProduct">
          <i class="bi bi-plus"></i> Create Product
        </button>
      </div>
    </div>

    <div v-else class="row g-4">
      <div
        v-for="product in publisherProducts"
        :key="product.id"
        class="col-lg-6 col-xl-4"
      >
        <div
          class="card h-100 card-hover"
          @click="viewProductDetail(product.id)"
          :data-cy="`product-card-${product.id}`"
        >
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h3 class="card-title mb-0">{{ product.name }}</h3>
              <span
                class="badge rounded-pill"
                :class="getStatusClass(product.status)"
              >
                {{ product.status }}
              </span>
            </div>

            <div class="mt-auto">
              <!-- 2×2 metric grid: counts on top, outcomes / financial below.
                   Bootstrap's row+col layout gives equal column widths and consistent gutters. -->
              <div class="row g-2 text-muted small">
                <div class="col-6">
                  <i class="bi bi-diagram-3 me-1"></i>{{ product.workflowCount }} workflows
                </div>
                <div class="col-6">
                  <i class="bi bi-people me-1"></i>{{ product.subscriberCount }} subscribers
                </div>
                <div
                  class="col-6"
                  :title="product.hasRuns ? '' : 'No completed runs yet for this product'"
                >
                  <i class="bi bi-check-circle me-1"></i>
                  <template v-if="product.hasRuns">{{ product.successRate }} success</template>
                  <template v-else>{{ product.successRate }}</template>
                </div>
                <div
                  class="col-6 metric-unavailable"
                  title="Revenue tracking coming soon — placeholder data"
                >
                  <i class="bi bi-currency-dollar me-1"></i>${{ product.monthlyRevenue.toLocaleString() }}/mo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Product Modal -->
    <CreateProductModal
      v-model:show="showCreateProductModal"
      @created="onProductCreated"
    />
  </div>
</template>

<style scoped lang="scss">
/* Product-specific styles - card hover is now handled by shared .card-hover class */
.product-item {
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  border: 1px solid var(--bs-border-color);

  &:hover {
    border-color: var(--bs-primary);
    background-color: var(--bs-tertiary-bg);
    transform: translateY(-1px);
    z-index: 1020;
    position: relative;
  }
}

/* PublisherProducts uses dual-layout approach for consistent mobile experience */

</style>
